import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { CreditCard, Plus, Trash2, Star, Loader2, X } from 'lucide-react';
import { formatApiError } from '../lib/apiError';

// Card brand logos/icons
const getCardBrandIcon = (brand) => {
  const brandLower = brand?.toLowerCase() || '';
  switch (brandLower) {
    case 'visa':
      return <span className="font-bold text-blue-600">VISA</span>;
    case 'mastercard':
      return <span className="font-bold text-orange-600">MC</span>;
    case 'amex':
      return <span className="font-bold text-blue-800">AMEX</span>;
    default:
      return <CreditCard className="w-6 h-6" />;
  }
};

const SavedPaymentMethods = ({ isStudioAdmin = false, onCardAdded }) => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingCard, setAddingCard] = useState(false);
  const [showCardDialog, setShowCardDialog] = useState(false);
  const [cardSetupData, setCardSetupData] = useState(null);
  const [publicKey, setPublicKey] = useState(null);
  const [ryftLoaded, setRyftLoaded] = useState(false);
  const [processingCard, setProcessingCard] = useState(false);
  const cardFormRef = useRef(null);
  const controllerRef = useRef(null);
  const cardFormInstanceRef = useRef(null);

  const fetchMethods = useCallback(async () => {
    try {
      const data = isStudioAdmin
        ? await apiGet('/api/studio/payment-methods')
        : await apiGet('/api/payment-methods');
      setMethods(data);
    } catch (e) {
      console.error('Failed to fetch payment methods:', e);
    } finally {
      setLoading(false);
    }
  }, [isStudioAdmin]);

  useEffect(() => {
    fetchMethods();
  }, [fetchMethods]);

  // Load RyftPay script - wait for it to be fully available
  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 20; // 20 * 250ms = 5 seconds max wait
    
    const checkRyft = () => {
      attempts++;
      console.log(`Checking for RyftPay SDK (attempt ${attempts})...`);
      
      if (window.Ryft && typeof window.Ryft.createController === 'function') {
        console.log('RyftPay SDK fully loaded and ready');
        setRyftLoaded(true);
        return;
      }
      
      if (attempts < maxAttempts) {
        setTimeout(checkRyft, 250);
      } else {
        console.error('RyftPay SDK failed to initialize after maximum attempts');
      }
    };
    
    // Start checking after a small delay
    const timer = setTimeout(checkRyft, 100);
    return () => clearTimeout(timer);
  }, []);

  // Fetch public key
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const data = await apiGet('/api/payments/config');
        if (data.public_key) {
          setPublicKey(data.public_key);
        }
      } catch (e) {
        console.error('Failed to fetch payment config:', e);
      }
    };
    fetchConfig();
  }, []);

  // Initialize RyftPay form when dialog opens
  useEffect(() => {
    if (showCardDialog && cardSetupData && publicKey) {
      const initRyft = async () => {
        // Wait for Ryft SDK to be available (with timeout)
        let waitAttempts = 0;
        const maxWaitAttempts = 40; // 40 * 100ms = 4 seconds
        
        while (!window.Ryft || typeof window.Ryft.createController !== 'function') {
          waitAttempts++;
          if (waitAttempts >= maxWaitAttempts) {
            console.error('RyftPay SDK not available after waiting');
            toast.error('Payment SDK not loaded. Please refresh the page.');
            return;
          }
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        // Wait for the DOM element to be available
        let domAttempts = 0;
        const maxDomAttempts = 20;
        
        while (!cardFormRef.current && domAttempts < maxDomAttempts) {
          domAttempts++;
          console.log(`Waiting for card form container (attempt ${domAttempts})...`);
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        if (!cardFormRef.current) {
          console.error('Card form container not found in DOM');
          toast.error('Failed to initialize payment form. Please try again.');
          return;
        }
        
        try {
          const { createController, createCardForm } = window.Ryft;
          
          console.log('Initializing RyftPay with:', {
            publicKey: publicKey.substring(0, 20) + '...',
            clientSecret: cardSetupData.client_secret.substring(0, 20) + '...'
          });
          
          const controller = createController({
            publicKey: publicKey,
            clientSecret: cardSetupData.client_secret,
            usage: 'setup-card'
          });
          
          controllerRef.current = controller;
          
          // Listen for events before mounting
          controller.on('ready', () => {
            console.log('RyftPay controller ready');
          });
          
          controller.on('error', (error) => {
            console.error('RyftPay controller error:', error);
            toast.error(error.message || 'Payment form error');
          });
          
          // Create card form - pass controller directly (not wrapped in object)
          const cardForm = createCardForm(controller);
          cardFormInstanceRef.current = cardForm;
          
          // Add card form specific error handling
          cardForm.on('error', (error) => {
            console.error('RyftPay card form error:', error);
          });
          
          cardForm.on('ready', () => {
            console.log('RyftPay card form ready and mounted');
            // Hide loading indicator
            const loadingIndicator = document.getElementById('ryft-loading-indicator');
            if (loadingIndicator) {
              loadingIndicator.style.display = 'none';
            }
          });
          
          // Clear the container before mounting (remove loading indicator)
          if (cardFormRef.current) {
            // Keep a reference to hide it after mount instead of removing
            const loadingIndicator = document.getElementById('ryft-loading-indicator');
            if (loadingIndicator) {
              loadingIndicator.style.display = 'none';
            }
          }
          
          // Mount to the DOM element
          cardForm.mount(cardFormRef.current);
          console.log('RyftPay card form mount called on element:', cardFormRef.current);
          
        } catch (e) {
          console.error('Failed to initialize RyftPay:', e);
          toast.error('Failed to initialize payment form: ' + (e.message || 'Unknown error'));
        }
      };
      
      // Small delay to ensure DOM is ready
      setTimeout(initRyft, 200);
    }
    
    return () => {
      // Cleanup on unmount
      if (controllerRef.current) {
        controllerRef.current = null;
      }
      if (cardFormInstanceRef.current) {
        cardFormInstanceRef.current = null;
      }
    };
  }, [showCardDialog, cardSetupData, publicKey]);

  const handleAddCard = async () => {
    if (!publicKey) {
      toast.error('Payment not configured. Please contact support.');
      return;
    }

    setAddingCard(true);
    try {
      const data = isStudioAdmin
        ? await apiPost('/api/studio/payment-methods/setup')
        : await apiPost('/api/payment-methods/setup');
      setCardSetupData(data);
      setShowCardDialog(true);
    } catch (e) {
      console.error('Failed to setup card:', e);
      const msg = formatApiError(e, 'Failed to setup card. Please try again.');
      toast.error(msg);
    } finally {
      setAddingCard(false);
    }
  };

  const handleConfirmCard = async () => {
    if (!cardFormInstanceRef.current) {
      toast.error('Payment form not initialized');
      return;
    }
    
    setProcessingCard(true);
    try {
      // Use cardForm.attemptPayment() for the SDK API
      const result = await cardFormInstanceRef.current.attemptPayment();
      console.log('RyftPay full result:', JSON.stringify(result, null, 2));
      
      // Check for successful payment session status
      const paymentSession = result.paymentSession;
      const isSuccess = paymentSession?.status === 'Approved' || 
                        paymentSession?.status === 'Captured' ||
                        paymentSession?.status === 'succeeded';
      
      if (isSuccess && paymentSession?.paymentMethod) {
        // Get the payment method ID from tokenizedDetails
        const paymentMethodId = paymentSession.paymentMethod.tokenizedDetails?.id;
        
        // Get card details
        const cardDetails = paymentSession.paymentMethod.card || {};
        
        console.log('Payment Method ID:', paymentMethodId);
        console.log('Card Details:', cardDetails);
        
        if (paymentMethodId) {
          // Save the payment method to our backend
          // Iter-42 Phase 2: also store the session id from this CIT setup so a
          // later MIT (Unscheduled) charge has the required `previousPayment.id`.
          const body = {
            payment_method_id: paymentMethodId,
            card_brand: cardDetails.scheme || 'unknown',
            card_last_four: cardDetails.last4 || '****',
            card_expiry_month: cardDetails.expiryMonth || 12,
            card_expiry_year: cardDetails.expiryYear || 2030,
            is_default: methods.length === 0,
            previous_payment_session_id: paymentSession?.id || cardSetupData?.payment_session_id || null,
          };
          if (isStudioAdmin) {
            await apiPost('/api/studio/payment-methods/save', { body });
          } else {
            await apiPost('/api/payment-methods/save', { body });
          }
          
          toast.success('Card added successfully');
          setShowCardDialog(false);
          setCardSetupData(null);
          fetchMethods();
          if (onCardAdded) onCardAdded(result);
        } else {
          console.error('No payment method ID found in tokenizedDetails');
          toast.error('Failed to get payment method details. Please try again.');
        }
      } else {
        console.error('Card verification failed with result:', result);
        const errorMessage = paymentSession?.error?.message || result.error?.message || 'Unknown error';
        toast.error(`Card verification failed: ${errorMessage}`);
      }
    } catch (e) {
      console.error('Failed to confirm card:', e);
      toast.error(e.message || 'Failed to verify card. Please try again.');
    } finally {
      setProcessingCard(false);
    }
  };

  const handleSetDefault = async (methodId) => {
    try {
      await apiPut('/api/payment-methods/{method_id}/default', { params: { method_id: methodId } });
      toast.success('Default payment method updated');
      fetchMethods();
    } catch (e) {
      toast.error('Failed to update default');
    }
  };

  const handleDelete = async (methodId) => {
    if (!window.confirm('Are you sure you want to remove this card?')) return;

    try {
      if (isStudioAdmin) {
        await apiDelete('/api/studio/payment-methods/{method_id}', { params: { method_id: methodId } });
      } else {
        await apiDelete('/api/payment-methods/{method_id}', { params: { method_id: methodId } });
      }
      toast.success('Card removed');
      fetchMethods();
    } catch (e) {
      toast.error('Failed to remove card');
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Saved Payment Methods
          </CardTitle>
          <CardDescription>
            {isStudioAdmin 
              ? 'Manage cards for subscription renewals'
              : 'Manage your saved cards for quick checkout and auto-renewal'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          ) : (
            <div className="space-y-4">
              {methods.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No saved cards</p>
                  <p className="text-sm">Add a card for faster checkout</p>
                </div>
              ) : (
                methods.map((method) => (
                  <div
                    key={method.id}
                    className={`flex items-center justify-between p-4 border rounded-lg ${
                      method.needs_reentry ? 'border-amber-400 bg-amber-50' : ''
                    }`}
                    data-testid={`payment-method-${method.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-8 flex items-center justify-center bg-gray-100 rounded">
                        {getCardBrandIcon(method.card_brand)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">
                            •••• •••• •••• {method.card_last_four}
                          </span>
                          {method.is_default && (
                            <Badge variant="secondary" className="text-xs">
                              <Star className="w-3 h-3 mr-1" />
                              Default
                            </Badge>
                          )}
                          {method.needs_reentry && (
                            <Badge
                              variant="destructive"
                              className="text-xs"
                              data-testid={`needs-reentry-${method.id}`}
                            >
                              Re-entry required
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Expires {method.card_expiry_month?.toString().padStart(2, '0')}/{method.card_expiry_year}
                          {method.nickname && ` • ${method.nickname}`}
                        </div>
                        {method.needs_reentry && (
                          <div className="text-xs text-amber-700 mt-1">
                            This card can no longer be charged. Please remove it and add it again.
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!method.is_default && !method.needs_reentry && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetDefault(method.id)}
                        >
                          Set Default
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(method.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}

              <Button
                variant="outline"
                className="w-full"
                onClick={handleAddCard}
                disabled={addingCard || !publicKey}
                data-testid="add-payment-method-btn"
              >
                {addingCard ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                {!publicKey ? 'Payment Not Configured' : 'Add New Card'}
              </Button>
              
              {!publicKey && (
                <p className="text-xs text-muted-foreground text-center">
                  Contact your platform administrator to enable payment processing.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Card Setup Dialog */}
      <Dialog open={showCardDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCardDialog(false);
          setCardSetupData(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Payment Method</DialogTitle>
            <DialogDescription>
              Enter your card details to save for future payments
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* RyftPay card form will mount here */}
            <div 
              ref={cardFormRef} 
              id="ryft-card-form-container"
              className="min-h-[120px] border rounded-lg p-4 bg-gray-50"
              data-testid="ryft-card-form"
            >
              {/* Loading indicator shows until form is actually mounted */}
              <div id="ryft-loading-indicator" className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">Loading payment form...</span>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowCardDialog(false);
                  setCardSetupData(null);
                }}
                disabled={processingCard}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleConfirmCard}
                disabled={processingCard || !ryftLoaded}
                data-testid="confirm-card-btn"
              >
                {processingCard ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Save Card'
                )}
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground text-center">
              Your card will be securely verified with a €0 authorization.
              No charges will be made.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SavedPaymentMethods;
